'use client'

import { useEffect, useState } from 'react'
import { getBillingPricingInfo } from '@/lib/api'
import type { BillingPricingInfo } from '@/lib/api'
import { useAuth } from './useAuth'

interface UseBillingPricingInfoResult {
  billingPricingInfo: BillingPricingInfo | null
  isLoading: boolean
  error: Error | null
}

interface BillingPricingInfoState extends UseBillingPricingInfoResult {
  userId: string | null
}

/**
 * One request per person at a time: /pricing mounts both plan families' cards (the hidden tab included), and each
 * reads this hook. A request that has answered is not reused; the next mount asks again.
 */
let inFlight: { userId: string | null; request: Promise<BillingPricingInfo> } | null = null

function requestPricingInfo(userId: string | null): Promise<BillingPricingInfo> {
  if (inFlight?.userId === userId) return inFlight.request
  const request = getBillingPricingInfo()
  inFlight = { userId, request }
  const settle = () => {
    if (inFlight?.request === request) inFlight = null
  }
  request.then(settle, settle)
  return request
}

/**
 * Fetches only the billing fields needed to render pricing CTAs.
 *
 * This intentionally does not use the shared billing store because the pricing
 * page should not wait on balances, BYOK state, invitations, or Autumn
 * enrichment that are only needed by account billing surfaces.
 */
export function useBillingPricingInfo(): UseBillingPricingInfoResult {
  const { userId, isAnonymous, isLoading: authLoading } = useAuth()
  const [state, setState] = useState<BillingPricingInfoState>({
    billingPricingInfo: null,
    isLoading: true,
    error: null,
    userId: null,
  })
  const shouldFetch = !authLoading && !isAnonymous && !!userId

  useEffect(() => {
    let isCurrent = true

    if (!shouldFetch) return

    const requestUserId = userId

    requestPricingInfo(requestUserId)
      .then(info => {
        if (!isCurrent) return
        setState({
          billingPricingInfo: info,
          isLoading: false,
          error: null,
          userId: requestUserId,
        })
      })
      .catch(err => {
        if (!isCurrent) return
        setState({
          billingPricingInfo: null,
          isLoading: false,
          error: err instanceof Error ? err : new Error('Failed to get pricing billing info'),
          userId: requestUserId,
        })
      })

    return () => {
      isCurrent = false
    }
  }, [shouldFetch, userId])

  const hasSettledForUser = shouldFetch && state.userId === userId

  return {
    billingPricingInfo: hasSettledForUser ? state.billingPricingInfo : null,
    isLoading: authLoading || (shouldFetch ? !hasSettledForUser || state.isLoading : false),
    error: hasSettledForUser ? state.error : null,
  }
}
